import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import DataSvc from "../../../../../../../services/dataSvc";
import { Schema } from "../../../schema";
import { CategoryAutocompleter } from "../../CategoryAutocompleter";

export const useCategorySuggestions = (
  locationId: number,
  onSelectSuggestion: (name: string) => void
) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const { watch } = useFormContext<Schema>();
  const values = watch();
  const allCategories = [
    values.primaryCategory,
    ...values.additionalCategories
  ].map((item) => item.name);

  const { data, refetch } = useQuery(
    ["categorySuggestions", locationId],
    async () => {
      const categories = await DataSvc.generateGbpCategorySuggestions(
        locationId
      );
      return categories;
    },
    { enabled: false }
  );

  const prevSuggestionsRef = useRef<string>("");

  useEffect(() => {
    if (!data) return;

    // Only returns categories which are valid and not already selected
    const filteredData =
      data?.filter((item: any) => {
        return (
          !allCategories.includes(item.name) &&
          CategoryAutocompleter.current?.checkIsValidCategory(item.name)
        );
      }) ?? [];

    const newFilteredDataString = JSON.stringify(filteredData);
    const haveSuggestionsChanged =
      prevSuggestionsRef.current !== newFilteredDataString;

    if (haveSuggestionsChanged) {
      prevSuggestionsRef.current = JSON.stringify(filteredData);
      setSuggestions(filteredData.map((item: any) => item.name));
    }
  }, [data, allCategories]);

  const removeSuggestion = async (option: string) => {
    const updatedSuggestions = suggestions.filter((item) => item !== option);
    setSuggestions(updatedSuggestions);
  };

  const handleSelectSuggestion = (name: string) => {
    onSelectSuggestion(name);
    removeSuggestion(name);
  };

  return { suggestions, handleSelectSuggestion, refetch };
};
